// src/store/bessOnboarding.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SdpPoint } from "../types/sdpTypes";

const CACHE_VERSION = 3;

const BESS_STORAGE_KEYS = {
  step1: "bessonboarding.step1",
  wizard: "bessonboarding.wizard",
  companyId: "bessonboarding.companyId",
} as const;

type BessOnboardingState = {
  // fetched lists
  injections: SdpPoint[];
  offtakes: SdpPoint[];
  // selections
  injectionEAN: string;
  offtakeEAN: string; // allow "none"
  siteName: string;
  selectedInjection: SdpPoint | null;
  selectedOfftake: SdpPoint | null;

  // step2 fields
  hasPartner: "yes" | "no" | "";
  partnerId: string;
  controller: string;
  controllerId: number | null;
  nominalPower: number | null;
  nominalEnergy: number | null;
  manufacturer: string;
  socMin: number | null;
  socMax: number | null;
  rte: number | null;
  cyclesPerYear: number | null;

  // contract fields (step3)
  desiredStartDate: string;
  actualStartDate: string;
  contractEndType: "auto" | "date";
  specifiedEndDate: string;

  // tracks whether the user has manually edited the site name
  siteNameManuallyEdited: boolean;

  // step1 confirmation
  flexConfirmed: boolean;

  // ui state
  loadingInjections: boolean;
  loadingOfftakes: boolean;
  errorInjections: string | null;
  errorOfftakes: string | null;
};

function hydratePersistedState(): Record<string, unknown> {
  try {
    const raw = localStorage.getItem(BESS_STORAGE_KEYS.step1);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed._cacheVersion !== CACHE_VERSION) {
      localStorage.removeItem(BESS_STORAGE_KEYS.step1);
      localStorage.removeItem(BESS_STORAGE_KEYS.wizard);
      return {};
    }
    return parsed;
  } catch {
    localStorage.removeItem(BESS_STORAGE_KEYS.step1);
    return {};
  }
}

const persisted = hydratePersistedState();

const STEP2_DEFAULTS = {
  socMin: 0,
  socMax: 100,
  rte: 88,
  cyclesPerYear: 600,
} as const;

const initialState: BessOnboardingState = {
  injections: [],
  offtakes: [],
  injectionEAN: (persisted.injectionEAN as string) ?? "",
  offtakeEAN: (persisted.offtakeEAN as string) ?? "",
  siteName: (persisted.siteName as string) ?? "",
  selectedInjection: (persisted.selectedInjection as SdpPoint) ?? null,
  selectedOfftake: (persisted.selectedOfftake as SdpPoint) ?? null,

  hasPartner: (persisted.hasPartner as "yes" | "no" | "") ?? "",
  partnerId: (persisted.partnerId as string) ?? "",
  controller: (persisted.controller as string) ?? "",
  controllerId: (persisted.controllerId as number) ?? null,
  nominalPower: (persisted.nominalPower as number) ?? null,
  nominalEnergy: (persisted.nominalEnergy as number) ?? null,
  manufacturer: (persisted.manufacturer as string) ?? "",
  socMin: (persisted.socMin as number) ?? STEP2_DEFAULTS.socMin,
  socMax: (persisted.socMax as number) ?? STEP2_DEFAULTS.socMax,
  rte: (persisted.rte as number) ?? STEP2_DEFAULTS.rte,
  cyclesPerYear: (persisted.cyclesPerYear as number) ?? STEP2_DEFAULTS.cyclesPerYear,

  // contract fields
  desiredStartDate: (persisted.desiredStartDate as string) ?? "",
  actualStartDate: (persisted.actualStartDate as string) ?? "",
  contractEndType: (persisted.contractEndType as "auto" | "date") ?? "auto",
  specifiedEndDate: (persisted.specifiedEndDate as string) ?? "",

  siteNameManuallyEdited: (persisted.siteNameManuallyEdited as boolean) ?? false,

  // step1 confirmation
  flexConfirmed: (persisted.flexConfirmed as boolean) ?? false,

  loadingInjections: false,
  loadingOfftakes: false,
  errorInjections: null,
  errorOfftakes: null,
};

const bessOnboardingSlice = createSlice({
  name: "bessOnboarding",
  initialState,
  reducers: {
    // lists
    setInjections(state, action: PayloadAction<SdpPoint[]>) {
      state.injections = action.payload;
    },
    setOfftakes(state, action: PayloadAction<SdpPoint[]>) {
      state.offtakes = action.payload;
    },
    setLoadingInjections(state, action: PayloadAction<boolean>) {
      state.loadingInjections = action.payload;
      if (action.payload) state.errorInjections = null;
    },
    setLoadingOfftakes(state, action: PayloadAction<boolean>) {
      state.loadingOfftakes = action.payload;
      if (action.payload) state.errorOfftakes = null;
    },
    setErrorInjections(state, action: PayloadAction<string | null>) {
      state.errorInjections = action.payload;
    },
    setErrorOfftakes(state, action: PayloadAction<string | null>) {
      state.errorOfftakes = action.payload;
    },

    // selections with full objects
    setInjectionSelection(state, action: PayloadAction<{ ean: string; injection: SdpPoint | null }>) {
      state.injectionEAN = action.payload.ean;
      state.selectedInjection = action.payload.injection;
      persist(state);
    },
    setOfftakeSelection(state, action: PayloadAction<{ ean: string; offtake: SdpPoint | null }>) {
      state.offtakeEAN = action.payload.ean;
      state.selectedOfftake = action.payload.offtake;
      persist(state);
    },
    setSiteName(state, action: PayloadAction<string>) {
      state.siteName = action.payload;
      persist(state);
    },
    setSiteNameManuallyEdited(state, action: PayloadAction<boolean>) {
      state.siteNameManuallyEdited = action.payload;
      persist(state);
    },

    // step2 setters
    setHasPartner(state, action: PayloadAction<"yes" | "no" | "">) {
      state.hasPartner = action.payload;
      // if user switches away from "yes" clear partnerId
      if (action.payload !== "yes") state.partnerId = "";
      persist(state);
    },
    setPartnerId(state, action: PayloadAction<string>) {
      state.partnerId = action.payload;
      persist(state);
    },
    setController(state, action: PayloadAction<{ name: string; id: number | null }>) {
      state.controller = action.payload.name;
      state.controllerId = action.payload.id;
      persist(state);
    },
    setNominalPower(state, action: PayloadAction<number | null>) {
      state.nominalPower = action.payload;
      persist(state);
    },
    setNominalEnergy(state, action: PayloadAction<number | null>) {
      state.nominalEnergy = action.payload;
      persist(state);
    },
    setManufacturer(state, action: PayloadAction<string>) {
      state.manufacturer = action.payload;
      persist(state);
    },
    setSocMin(state, action: PayloadAction<number | null>) {
      state.socMin = action.payload;
      persist(state);
    },
    setSocMax(state, action: PayloadAction<number | null>) {
      state.socMax = action.payload;
      persist(state);
    },
    setRte(state, action: PayloadAction<number | null>) {
      state.rte = action.payload;
      persist(state);
    },
    setCyclesPerYear(state, action: PayloadAction<number | null>) {
      state.cyclesPerYear = action.payload;
      persist(state);
    },

    // contract reducers (step3)
    setDesiredStartDate(state, action: PayloadAction<string>) {
      state.desiredStartDate = action.payload;
      persist(state);
    },
    setActualStartDate(state, action: PayloadAction<string>) {
      state.actualStartDate = action.payload;
      persist(state);
    },
    setContractEndType(state, action: PayloadAction<"auto" | "date">) {
      state.contractEndType = action.payload;
      // clear specified end date when switching to auto
      if (action.payload === "auto") state.specifiedEndDate = "";
      persist(state);
    },
    setSpecifiedEndDate(state, action: PayloadAction<string>) {
      state.specifiedEndDate = action.payload;
      persist(state);
    },
    setFlexConfirmed(state, action: PayloadAction<boolean>) {
      state.flexConfirmed = action.payload;
      persist(state);
    },

    resetStep1(state) {
      state.injectionEAN = "";
      state.selectedInjection = null;
      state.offtakeEAN = "";
      state.selectedOfftake = null;
      state.siteName = "";
      state.siteNameManuallyEdited = false;
      state.flexConfirmed = false;
      state.hasPartner = "";
      state.partnerId = "";
      state.controller = "";
      state.controllerId = null;
      state.nominalPower = null;
      state.nominalEnergy = null;
      state.manufacturer = "";
      state.socMin = STEP2_DEFAULTS.socMin;
      state.socMax = STEP2_DEFAULTS.socMax;
      state.rte = STEP2_DEFAULTS.rte;
      state.cyclesPerYear = STEP2_DEFAULTS.cyclesPerYear;
      state.desiredStartDate = "";
      state.actualStartDate = "";
      state.contractEndType = "auto";
      state.specifiedEndDate = "";
      persist(state);
    },
    clearDownstreamSteps(state) {
      state.hasPartner = "";
      state.partnerId = "";
      state.controller = "";
      state.controllerId = null;
      state.nominalPower = null;
      state.nominalEnergy = null;
      state.manufacturer = "";
      state.socMin = STEP2_DEFAULTS.socMin;
      state.socMax = STEP2_DEFAULTS.socMax;
      state.rte = STEP2_DEFAULTS.rte;
      state.cyclesPerYear = STEP2_DEFAULTS.cyclesPerYear;
      state.desiredStartDate = "";
      state.actualStartDate = "";
      state.contractEndType = "auto";
      state.specifiedEndDate = "";
      state.siteNameManuallyEdited = false;
      state.flexConfirmed = false;
      persist(state);
    },
    resetApiData(state) {
      state.injections = [];
      state.offtakes = [];
      state.loadingInjections = false;
      state.loadingOfftakes = false;
      state.errorInjections = null;
      state.errorOfftakes = null;
    },
    prefillFromContractData(
      state,
      action: PayloadAction<{
        partner_id: number | null;
        controller_id: number | null;
        controller_name: string | null;
        // Backend guard signal: true when the site has a certified controller assigned.
        // Redundant with controller_id != null (which drives the wizard's live gate), so the
        // wizard keys off controllerId; kept here to mirror the endpoint's response shape.
        hasCertifiedController?: boolean;
        nominal_power: number | null;
        nominal_energy: number | null;
        manufacturer: string | null;
        soc_min: number | null;
        soc_max: number | null;
        rte: number | null;
        cycles_per_year: number | null;
        start_date: string | null;
        end_date: string | null;
        site_name?: string | null;
      }>
    ) {
      const d = action.payload;
      state.hasPartner = d.partner_id != null ? "yes" : "";
      state.partnerId = d.partner_id != null ? String(d.partner_id) : "";
      state.controllerId = d.controller_id;
      state.controller = d.controller_name ?? "";
      state.nominalPower = d.nominal_power;
      state.nominalEnergy = d.nominal_energy;
      state.manufacturer = d.manufacturer ?? "";
      state.socMin = d.soc_min ?? STEP2_DEFAULTS.socMin;
      state.socMax = d.soc_max ?? STEP2_DEFAULTS.socMax;
      state.rte = d.rte ?? STEP2_DEFAULTS.rte;
      state.cyclesPerYear = d.cycles_per_year ?? STEP2_DEFAULTS.cyclesPerYear;
      state.desiredStartDate = d.start_date ?? "";
      state.contractEndType = d.end_date ? "date" : "auto";
      state.specifiedEndDate = d.end_date ?? "";
      if (d.site_name && d.site_name.trim() !== "") {
        state.siteName = d.site_name;
        state.siteNameManuallyEdited = true;
      }
      persist(state);
    },
  },
});

function persist(state: BessOnboardingState) {
  const payload = {
    _cacheVersion: CACHE_VERSION,
    injectionEAN: state.injectionEAN,
    offtakeEAN: state.offtakeEAN,
    siteName: state.siteName,
    selectedInjection: state.selectedInjection,
    selectedOfftake: state.selectedOfftake,
    siteNameManuallyEdited: state.siteNameManuallyEdited,
    hasPartner: state.hasPartner,
    partnerId: state.partnerId,
    controller: state.controller,
    controllerId: state.controllerId,
    nominalPower: state.nominalPower,
    nominalEnergy: state.nominalEnergy,
    manufacturer: state.manufacturer,
    socMin: state.socMin,
    socMax: state.socMax,
    rte: state.rte,
    cyclesPerYear: state.cyclesPerYear,
    desiredStartDate: state.desiredStartDate,
    actualStartDate: state.actualStartDate,
    contractEndType: state.contractEndType,
    specifiedEndDate: state.specifiedEndDate,
    flexConfirmed: state.flexConfirmed,
  };
  localStorage.setItem(BESS_STORAGE_KEYS.step1, JSON.stringify(payload));
}

export function clearAllBessOnboardingCache() {
  localStorage.removeItem(BESS_STORAGE_KEYS.step1);
  localStorage.removeItem(BESS_STORAGE_KEYS.wizard);
  localStorage.removeItem(BESS_STORAGE_KEYS.companyId);
}

export { BESS_STORAGE_KEYS };

export const {
  setInjections,
  setOfftakes,
  setLoadingInjections,
  setLoadingOfftakes,
  setErrorInjections,
  setErrorOfftakes,
  setInjectionSelection,
  setOfftakeSelection,
  setSiteName,
  setSiteNameManuallyEdited,
  setHasPartner,
  setPartnerId,
  setController,
  setNominalPower,
  setNominalEnergy,
  setManufacturer,
  setSocMin,
  setSocMax,
  setRte,
  setCyclesPerYear,
  setDesiredStartDate,
  setActualStartDate,
  setContractEndType,
  setSpecifiedEndDate,
  setFlexConfirmed,
  resetStep1,
  clearDownstreamSteps,
  resetApiData,
  prefillFromContractData,
} = bessOnboardingSlice.actions;

export default bessOnboardingSlice.reducer;
